import { FormAccordion } from '@strata/react-form-section';
import { InlineMessage } from '@strata/react-inline-message';
import { QuestionForm } from '@strata/react-question-tree';
import { SuccessToast, WarningToast } from '@strata/react-toast-message';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FormExceptionDialog } from 'components/referralForm/dialogs/FormExceptionDialog';
import { useFormResetRef } from 'components/referralForm/FormHelpers';
import { SectionLoadingSpinner } from 'components/referralForm/SectionLoadingSpinner';
import { fetchQuestionTree } from 'store/referralForm/questionSlice';
import {
    enableSection,
    nextSection,
    setClinicalPolicyAnswers,
} from 'store/referralForm/referralFormSlice';

export const ClinicalInformation = ({ section, isOpen }) => {
    const dispatch = useDispatch();
    const { nodes } = useSelector((state) => state.question);
    const { capturedData, activeSection } = useSelector((state) => state.referralForm);

    const [exceptionAnswers, setExceptionAnswers] = useState([]);

    const answers = capturedData.clinicalPolicyAnswers;
    const formRef = useFormResetRef(isOpen);
    const inputRef = useRef(null);
    useEffect(() => {
        if (isOpen && section === activeSection) {
            setTimeout(
                () => inputRef.current?.focus(),
                answers.length > 0 ? 0 : FormAccordion.AnimateDuration,
            );
        }
    }, [answers, isOpen, activeSection, section]);

    // Fetch question tree info if not in store
    const { productServiceOid, diagnosisOid, payerId, prescriberNpi } = capturedData;
    useEffect(() => {
        if (productServiceOid && diagnosisOid && payerId && prescriberNpi) {
            dispatch(
                fetchQuestionTree({ productServiceOid, diagnosisOid, payerId, prescriberNpi }),
            );
        }
    }, [productServiceOid, diagnosisOid, payerId, prescriberNpi, dispatch]);

    // Check for diagnosis exception
    if (capturedData?.diagnosisException) {
        if (section === activeSection) {
            dispatch(nextSection(section));
        }
        return (
            <InlineMessage
                intent={InlineMessage.Intent.WARNING}
                label="Clinical information is not available for a diagnosis exception."
            />
        );
    }

    return !nodes ? (
        <SectionLoadingSpinner />
    ) : (
        <>
            <FormExceptionDialog
                exceptionType={FormExceptionDialog.ExceptionTypes.POLICY}
                isOpen={exceptionAnswers.length > 0}
                onSubmit={(clinicalPolicyException) => {
                    dispatch(setClinicalPolicyAnswers(exceptionAnswers, clinicalPolicyException));
                    WarningToast({ message: 'Clinical exception will be sent for review.' });
                    setExceptionAnswers([]);
                    dispatch(nextSection(section, true));
                }}
                onSecondaryButtonClick={() => {
                    setExceptionAnswers([]);
                    inputRef.current?.focus();
                }}
            />
            <QuestionForm
                nodes={nodes}
                answers={answers}
                hasSubmittedException={capturedData.clinicalPolicyException}
                benefitAttributes={capturedData.benefitAttributes}
                showClinicalPolicyMessaging
                onSectionSubmit={(ans) => {
                    dispatch(enableSection(section));
                    dispatch(setClinicalPolicyAnswers(ans));
                }}
                onFormCompleted={() => dispatch(nextSection(section))}
                onException={(ans) => {
                    if (exceptionAnswers.length === 0) {
                        setExceptionAnswers(ans);
                    }
                }}
                onAutoAccept={() => {
                    if (section === activeSection) {
                        SuccessToast({ message: 'Clinical information is not required.' });
                        dispatch(nextSection(section));
                    }
                }}
                formRef={formRef}
                inputRef={inputRef}
            />
        </>
    );
};

ClinicalInformation.propTypes = {
    section: PropTypes.number.isRequired,
    isOpen: PropTypes.bool.isRequired,
};
